<template>
  <div v-if="!isMobileLoading" ref="buildingsContainer" class="buildings-container">
    <div class="buildings-list">
      <MobileBuildingWrapper
        v-for="building in buildingsList"
        :key="building.buildingKey"
        :building="building"
        @open-building="openBuilding"
      />
    </div>

    <el-dialog
      v-model="showBuyPearlModal"
      class="crafting buyPearlModal"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :fullscreen="true"
      :title="$t(selectedBuilding?.buildingKey || '', { count: 2 })"
      :append-to-body="true"
      :center="true"
      @close="store.openedBuilding = null"
    >
      <BuyPearlModal :token="Tokens.pearl" :is-building="true" />
    </el-dialog>

    <el-dialog
      v-if="selectedBuilding && selectedBuilding.miningToken"
      v-model="showBuyLicenseModal"
      class="crafting"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :fullscreen="$device.isMobile"
      :title="$t(selectedBuilding?.buildingKey || '')"
      :append-to-body="true"
      :center="true"
      @close="store.openedBuilding = null"
    >
      <BuyLicenseModal
        :token="selectedBuilding.miningToken"
        :description="$t(selectedBuilding.descriptionKey)"
        :is-building="true"
      />
    </el-dialog>

    <el-dialog
      v-if="selectedBuilding"
      v-model="showCraftingModal"
      class="crafting produce"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :fullscreen="true"
      :title="$t(selectedBuilding?.buildingKey || '')"
      :append-to-body="true"
      :center="true"
      destroy-on-close
      @close="
        () => {
          store.openedBuilding = null;
          showCraftingModal = false;
        }
      "
    >
      <CraftingModal
        :selected-building="selectedBuilding"
        @closed="
          () => {
            store.openedBuilding = null;
            showCraftingModal = false;
          }
        "
      />
    </el-dialog>

    <el-dialog
      v-model="showUserProfileModal"
      :append-to-body="true"
      class="myProfileModal"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :title="$t('profilePageName')"
      :center="true"
      destroy-on-close
      fullscreen
      @close="store.openedBuilding = null"
    >
      <MyProfileModal />
    </el-dialog>

    <StorageWrapper
      :is-storage-selected="showStorageModal"
      :description="'storage_description'"
      @closed="showStorageModal = false"
    />

    <SellerModal @closed="showSellerModal = false" />

    <ClientOnly>
      <TavernMainContainer v-model="showTavernModal" @closed="showTavernModal = false" />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import useEnvs from '~/composables/useEnvs';
import type { BuildingProps, RecipeProps } from '~/types/crafting';
import { MINING_BUILDINGS } from '~/utils/constants/miningBuildings';
import { apiUrls, Buildings, Tokens } from '~/utils/constants';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import type { ITokenModel, IUserModel } from '~/types/apiService';
import { useGoToBuildingBus } from '~/composables/useGoToBuildingBus';
import { EXPEDITION_BUILDINGS } from '~/utils/constants/expeditionBuildings';
import { useRentEventStore } from '~/stores/rent-event-store';

const { apiUrl } = useEnvs();
const buildingsContainer = ref();
const buildingsList = ref<BuildingProps[]>([]);
const store = useMainStore();
const { address } = useWeb3ModalAccount();
const { login } = useAuth();
const bus = useGoToBuildingBus();
const halloween = useHalloweenStore();
const rent = useRentEventStore();

const { data: userData } = useNuxtData<IUserModel>('userData');
useFetch<ITokenModel[]>(apiUrls.token.tokens, {
  key: 'all-tokens',
  baseURL: apiUrl,
  immediate: true
});

const { openedBuilding } = storeToRefs(store);

watch(openedBuilding, (building) => {
  if (!building) selectedBuilding.value = null;
});

const selectedRecipes = ref<RecipeProps[] | null>(null);
const selectedBuilding = ref<BuildingProps | null>(null);
const showCraftingModal = ref(false);
const showBuyPearlModal = ref(false);
const showBuyLicenseModal = ref(false);
const showUserProfileModal = ref(false);
const showSellerModal = ref(false);
const showTavernModal = ref(false);
const showStorageModal = ref(false);

let buildingToOpenAfterClose: BuildingProps | null = null;
let witchAutoCloseTimeout: ReturnType<typeof setTimeout> | null = null;

const openBuilding = async (payload: { building: BuildingProps; recipes: RecipeProps[] | null }) => {
  selectedBuilding.value = payload.building;
  selectedRecipes.value = payload.recipes;

  store.openedBuilding = payload.building.buildingKey;

  if (selectedBuilding.value.buildingKey === Buildings.WitchHut) {
    buildingToOpenAfterClose = payload.building;
    selectedBuilding.value = null;
    await nextTick();
    halloween.$state.isWitchWelcomeModalOpen = true;

    witchAutoCloseTimeout = setTimeout(() => {
      halloween.$state.isWitchWelcomeModalOpen = false;
      witchAutoCloseTimeout = null;
    }, 4000);

    return;
  }

  if (
    selectedBuilding.value.buildingKey === Buildings.Forest ||
    selectedBuilding.value.buildingKey === Buildings.Cave
  ) {
    halloween.handleExpeditionLocationSelect(selectedBuilding.value.buildingKey);
    return;
  }

  if (selectedBuilding.value.buildingKey === Buildings.Barbershop) {
    if (!userData.value) {
      login();
      store.openedBuilding = null;

      return;
    }

    showUserProfileModal.value = true;
  }

  if (selectedBuilding.value.buildingKey === Buildings.Seller) {
    showSellerModal.value = true;
  }

  if (selectedBuilding.value.buildingKey === Buildings.Tavern) {
    showTavernModal.value = true;
  }

  if (selectedBuilding.value.buildingKey === Buildings.Manor) {
    if (localStorage.getItem('rent-announced')) {
      rent.$state.isManorModalOpen = true;
    } else {
      rent.$state.announceModalOpen = true;
    }
  }

  if (selectedBuilding.value.buildingKey === 'dock') {
    showBuyPearlModal.value = true;
    return;
  }

  if (selectedBuilding.value.miningToken) {
    showBuyLicenseModal.value = true;
    return;
  }

  if (selectedRecipes.value?.length) {
    showCraftingModal.value = true;
  }
};

const handleWitchModalClosed = () => {
  if (buildingToOpenAfterClose) {
    selectedBuilding.value = buildingToOpenAfterClose;

    if (selectedRecipes.value?.length) {
      showCraftingModal.value = true;
    }

    buildingToOpenAfterClose = null;
  }
};

watch(
  () => halloween.$state.isWitchWelcomeModalOpen,
  (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      if (witchAutoCloseTimeout) {
        clearTimeout(witchAutoCloseTimeout);
        witchAutoCloseTimeout = null;
      }

      handleWitchModalClosed();
    }
  }
);

const goToBuildingByHashMobile = async (hash: string) => {
  if (hash === selectedBuilding.value?.buildingHash) return;
  selectedBuilding.value = null;
  await nextTick();
  selectedBuilding.value = buildingsList.value.find((item) => item.buildingHash === hash) ?? null;
  if (selectedBuilding.value) {
    store.openedBuilding = selectedBuilding.value.buildingKey;
    showCraftingModal.value = true;
  }
};

bus.on((hash) => {
  goToBuildingByHashMobile(hash);
});

const openStorageByMobile = async () => {
  if (!userData.value) {
    await login();
    return;
  }

  showStorageModal.value = true;
};

const openMiningBuilding = async (tokenName: string) => {
  if (tokenName === 'pearl' || tokenName === 'blackPearl') {
    buildingToOpenAfterClose = {
      buildingName: '',
      buildingKey: Buildings.Dock,
      buildingHash: '',
      buildingId: '',
      descriptionKey: ''
    } as BuildingProps;
    showCraftingModal.value = false;
    await nextTick();
    selectedBuilding.value = buildingToOpenAfterClose;
    store.openedBuilding = selectedBuilding.value.buildingKey;
    showBuyPearlModal.value = true;
    buildingToOpenAfterClose = null;
    return;
  }
  const miningBuilding = buildingsList.value.find((item) => item.miningToken === tokenName) ?? null;
  if (!miningBuilding) return;
  buildingToOpenAfterClose = miningBuilding;
  showCraftingModal.value = false;
  await nextTick();
  selectedBuilding.value = buildingToOpenAfterClose;
  store.openedBuilding = selectedBuilding.value.buildingKey;
  showBuyLicenseModal.value = true;
  buildingToOpenAfterClose = null;
};

provide('goToMiningBuildingByHashMobile', openMiningBuilding);
provide('goToBuildingByHashMobile', goToBuildingByHashMobile);
provide('openStorageByMobile', openStorageByMobile);
provide('showSellerModal', showSellerModal);
provide('building', selectedBuilding);
const isMobileLoading = defineModel<boolean>();

watch(
  address,
  async () => {
    store.currentDistrict = null;

    const miningBuildings = Object.entries(MINING_BUILDINGS).map(([key, value]) => ({
      ...value,
      miningToken: key as Tokens
    }));

    await fetch(apiUrl + '/crafting/buildings?withRecipes=true')
      .then((response) => response.json())
      .then((data) => {
        isMobileLoading.value = false;
        buildingsList.value = data
          .filter((building: BuildingProps) => building.buildingKey && !building.buildingKey.includes('mine'))
          .concat({
            buildingName: '',
            buildingKey: Buildings.Dock,
            buildingHash: '',
            buildingId: '',
            descriptionKey: ''
          })
          .concat({
            buildingName: '',
            buildingKey: Buildings.Barbershop,
            buildingHash: '',
            buildingId: '',
            descriptionKey: ''
          })
          .concat({
            buildingName: '',
            buildingKey: Buildings.Seller,
            buildingHash: '',
            buildingId: '',
            descriptionKey: ''
          })
          .concat({
            buildingName: '',
            buildingKey: Buildings.Tavern,
            buildingHash: '',
            buildingId: '',
            descriptionKey: ''
          })
          .concat({
            buildingName: '',
            buildingKey: Buildings.Manor,
            buildingHash: '',
            buildingId: '',
            descriptionKey: ''
          })
          .concat(EXPEDITION_BUILDINGS)
          .concat(miningBuildings);
      });
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.buildings-container {
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(100% - 60px);

  @media (max-width: 376px) {
    height: calc(100% - 30px);
  }

  .buildings-list {
    display: flex;
    flex-direction: column;
    gap: 62px;
    margin: 60px 16px 0;

    @media (max-width: 376px) {
      margin: 60px 8px 0;
    }

    .building-wrapper {
      opacity: 1;
    }
  }
}

.margin {
  margin-top: 44px;

  @media (max-width: 376px) {
    margin-top: 14px;
  }
}
</style>
